/**
 * Per-provider-instance TTL cache for resolved data sources.
 *
 * Keyed by `deriveCacheKey(method, url, query, body)`. Entries expire after
 * their `ttlMs` and are lazily evicted on `get()`. Cache instances are
 * owned by the provider via `useRef` (child SAD Appendix C explicitly
 * rejects module-scoped singletons — those caused stale-data bugs in the
 * POC across flow transitions).
 *
 * The cache is intentionally plain and dependency-free: the provider
 * constructs it in a `useRef` initializer, reads/writes synchronously
 * inside its reducer / effect, and clears it on unmount. No LRU, no size
 * limit — DSM caches are short-lived (one flow's worth of sources) and
 * capped by `ttlMs`.
 */

export interface CacheEntry<T = unknown> {
  readonly data: T;
  readonly expiresAt: number;
  /**
   * When the entry was written, so a consumer serving a cache hit can report
   * how old the data is rather than when it read it. `expiresAt` cannot answer
   * that: it is the write time plus a TTL the reader does not know.
   */
  readonly storedAt: number;
}

export interface DataSourceCache<T = unknown> {
  readonly get: (key: string) => T | undefined;
  /**
   * Like {@link DataSourceCache.get}, but keeps the entry's write time. Same
   * expiry behavior, so a caller choosing between them is choosing whether it
   * needs the age, not whether it gets a live value.
   */
  readonly getEntry: (key: string) => CacheEntry<T> | undefined;
  readonly set: (key: string, data: T, ttlMs: number) => void;
  readonly delete: (key: string) => void;
  readonly clear: () => void;
  readonly size: () => number;
}

/**
 * Allow tests to inject a deterministic clock. Defaults to `Date.now`.
 */
export interface CacheOptions {
  readonly now?: () => number;
}

export function createDataSourceCache<T = unknown>(
  options: CacheOptions = {},
): DataSourceCache<T> {
  const store = new Map<string, CacheEntry<T>>();
  const now = options.now ?? Date.now;

  const liveEntry = (key: string): CacheEntry<T> | undefined => {
    const entry = store.get(key);
    if (!entry) {
      return undefined;
    }
    if (entry.expiresAt <= now()) {
      store.delete(key);
      return undefined;
    }
    return entry;
  };

  return {
    get(key) {
      return liveEntry(key)?.data;
    },
    getEntry(key) {
      return liveEntry(key);
    },
    set(key, data, ttlMs) {
      if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
        return;
      }
      const storedAt = now();
      store.set(key, { data, expiresAt: storedAt + ttlMs, storedAt });
    },
    delete(key) {
      store.delete(key);
    },
    clear() {
      store.clear();
    },
    size() {
      return store.size;
    },
  };
}

/**
 * Build a stable cache key from an interpolated request descriptor.
 *
 * - Method is upper-cased.
 * - Query keys are sorted lexicographically; array values are serialized
 *   as `a,b,c` (matches `interpolate()`'s mixed-template coercion rule,
 *   ADR-12 §query-param).
 * - Body is stable-stringified — object keys sorted at every depth — so
 *   structurally equivalent bodies hash to the same key regardless of
 *   author order.
 * - Headers are stable-stringified (case-insensitive header-name
 *   normalization). They are part of the request identity because
 *   tenant / auth / locale / personalization / content-negotiation
 *   semantics live there — two requests that differ only in
 *   `x-tenant-id` or `accept-language` produce different responses
 *   and **must not** share a cache entry (review #1, R. Vazquez).
 * - `responsePath` is part of the cached payload's identity because
 *   the cache stores the **already-extracted** payload, not the raw
 *   response. Two declarations with the same request but different
 *   `responsePath` (e.g. `data` vs `_facets.degreeTypes`) would
 *   otherwise alias and silently bind the wrong shape to consumers
 *   (review #1, critical).
 *
 * Bodies / headers that fail JSON.stringify (e.g. circular references)
 * fall back to the literal string "<unserializable>"; this is
 * conservatively safe — two such payloads share a key and will
 * over-cache, but DSL configs never produce circular structures in
 * practice (plain JSON → structurally acyclic) so the fallback mostly
 * flags a programmer error.
 */
export function deriveCacheKey(
  method: string,
  url: string,
  query?: Readonly<Record<string, string | readonly string[]>>,
  body?: unknown,
  headers?: Readonly<Record<string, string>>,
  responsePath?: string,
): string {
  const queryPart = serializeQuery(query);
  const bodyPart = body === undefined ? '' : stableStringify(body);
  const headersPart = serializeHeaders(headers);
  const pathPart = responsePath ?? '';
  return `${method.toUpperCase()} ${url}?${queryPart} ${bodyPart} h:${headersPart} rp:${pathPart}`;
}

/**
 * Serialize headers with lowercased names and sorted keys so
 * `{ 'X-Tenant': 'a' }` and `{ 'x-tenant': 'a' }` produce the same
 * key (HTTP header names are case-insensitive per RFC 7230 §3.2).
 * Header values stay as authored — they are case-sensitive.
 */
function serializeHeaders(
  headers: Readonly<Record<string, string>> | undefined,
): string {
  if (!headers) return '';
  const normalized: Record<string, string> = {};
  for (const key of Object.keys(headers)) {
    normalized[key.toLowerCase()] = headers[key];
  }
  return stableStringify(normalized);
}

function serializeQuery(
  query: Readonly<Record<string, string | readonly string[]>> | undefined,
): string {
  if (!query) {
    return '';
  }
  const keys = Object.keys(query).sort();
  const parts: string[] = [];
  for (const key of keys) {
    const value = query[key];
    const joined = Array.isArray(value) ? value.join(',') : String(value);
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(joined)}`);
  }
  return parts.join('&');
}

/**
 * Deterministic JSON stringify — sorts object keys at every depth.
 * Handles primitives, arrays, and plain objects. Falls back to
 * "<unserializable>" on cycles / `JSON.stringify` throw.
 *
 * Exported so callers that need a content-addressed identity for a
 * declaration (e.g. the DataSourceManager reconcile diff) hash it the
 * same way the cache key does, rather than re-deriving a second
 * serializer that could drift from this one.
 */
export function stableStringify(value: unknown): string {
  try {
    return stringify(value, new WeakSet<object>());
  } catch {
    return '<unserializable>';
  }
}

function stringify(value: unknown, stack: WeakSet<object>): string {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : 'null';
  }
  if (typeof value === 'boolean' || typeof value === 'string') {
    return JSON.stringify(value);
  }
  if (typeof value === 'undefined' || typeof value === 'function') {
    return 'null';
  }
  if (typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (stack.has(value)) {
    return '"<cycle>"';
  }
  stack.add(value);
  try {
    if (Array.isArray(value)) {
      const items = value.map((item) => stringify(item, stack));
      return `[${items.join(',')}]`;
    }
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    const parts = keys.map(
      (k) => `${JSON.stringify(k)}:${stringify(obj[k], stack)}`,
    );
    return `{${parts.join(',')}}`;
  } finally {
    stack.delete(value);
  }
}
